'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import Icon from '@/components/ui/AppIcon';

import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface Props {
  onSwitchToRegister: () => void;
}

export default function LoginForm({ onSwitchToRegister }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { signIn } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      router.push('/member-dashboard');
    } catch (err: any) {
      setError('email', { message: err?.message || 'Invalid credentials — please check your email and password' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-7">
        <h2 className="text-2xl font-bold text-foreground mb-1.5">Welcome back</h2>
        <p className="text-sm text-muted-foreground">Sign in to your Richésse account</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        {/* Email */}
        <div>
          <label className="richesse-label">Email Address</label>
          <input
            type="email"
            autoComplete="email"
            placeholder="maria@richesse.global"
            className={`richesse-input ${errors.email ? 'border-danger' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-danger flex items-center gap-1.5">
              <Icon name="ExclamationCircleIcon" size={14} />
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="richesse-label mb-0">Password</label>
            <button type="button" className="text-xs text-primary hover:text-primary-dark transition-colors font-medium">
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={`richesse-input pr-10 ${errors.password ? 'border-danger' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-danger flex items-center gap-1.5">
              <Icon name="ExclamationCircleIcon" size={14} />
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2.5">
          <input
            type="checkbox"
            id="rememberMe"
            className="w-4 h-4 rounded border-border accent-primary"
            {...register('rememberMe')}
          />
          <label htmlFor="rememberMe" className="text-sm text-muted-foreground">
            Remember me for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{ minHeight: '48px' }}
        >
          {loading ? (
            <>
              <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In to Richésse'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Not a member yet?{' '}
        <button onClick={onSwitchToRegister} className="text-primary font-semibold hover:text-primary-dark transition-colors">
          Join Richésse
        </button>
      </p>
    </div>
  );
}